import React, {
  ChangeEvent,
  FormEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Button } from '@/components/ui/button'
import { GridColumn as Column, Grid, GridCellProps } from '@progress/kendo-react-grid'
import { ClientDetailedDto } from 'api/Clients/Types/ClientDetailedDto'
import { useAppDispatch, useAppSelector } from 'store'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'

import { CreateAMLAdjustment as CreateAMLAdjustmentDto } from '../../../../api/Clients/Types/CreateAMLAdjustment'
import {
  FormBaseButtonContainer,
  FormBaseColumn,
  FormBaseFieldLabel,
  FormBaseRow,
  FormBaseSectionBlock,
} from '../../../../components/Form/Form.styles'
import MoreButton from '../../../../components/MoreButton/MoreButton'
import { errorToast, successToast, ToastContext } from '../../../../components/Toast/Toast'
import {
  fetchAMLAdjustmentReasons,
  fetchAMLCategoriesTypes,
} from '../../../../store/clients/clients.actions'
import { useCallbackWithForceRefresh } from '../../../../utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from '../../../../utils/api/useFetch'
import { isEmpty } from '../../../../utils/is-empty'
import {
  FormFieldBlock,
  FormFieldGroup,
  FormFieldLabel,
} from '../../../Administration/Administration.styles'
import { Container } from '../Clients.styles'

interface CreateAMLAdjustment extends Omit<CreateAMLAdjustmentDto, 'newScore'> {
  newScore: string
}

interface ClientDetailsAMLScoreProps {
  data: ClientDetailedDto
  onHistoryOpen(): void
}

const useClientDetailsAMLScoreFetch = (props: ClientDetailsAMLScoreProps) => {
  const { data } = props

  const dispatch = useAppDispatch()
  const clientsApiClient = useApiClient(ClientsApiClient)

  const adjustmentReasons = useAppSelector((state) => state.clients.adjustmentReasons)

  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh(async () => {
    return clientsApiClient.getClientAMLDetails(data.id)
  }, [data.id, dispatch]) // eslint-disable-line react-hooks/exhaustive-deps
  const { data: amlData } = useFetchOne(fetchData)

  useEffect(() => {
    if (isEmpty(adjustmentReasons)) {
      dispatch(fetchAMLAdjustmentReasons(clientsApiClient))
    }
  }, [dispatch, adjustmentReasons]) // eslint-disable-line react-hooks/exhaustive-deps

  const calculateScore = useMemo(() => {
    return {
      score: amlData?.amlFields.reduce((acc, f) => acc + f.currentValue.score, 0),
      adjustedScore: amlData?.manualAdjustments?.values?.reduce((acc, f) => {
        if (f.valid) {
          return acc + f.score
        }
        return acc
      }, 0),
    }
  }, [amlData])

  return { amlData, adjustmentReasons, calculateScore, forceRefresh }
}

const useClientDetailsAMLScoreMutate = (props: ClientDetailsAMLScoreProps) => {
  const { data } = props

  const clientsApiClient = useApiClient(ClientsApiClient)
  const setToast = useContext(ToastContext)

  const [formValues, setFormValues] = useState<Partial<CreateAMLAdjustment>>()

  const handleSubmit = async (event: FormEvent, callback: () => void) => {
    event.preventDefault()

    const newScore = Number.parseInt(formValues?.newScore?.toString() || '', 10)
    const amlValuePredefinedReasonId = Number.parseInt(
      formValues?.amlValuePredefinedReasonId?.toString() || '',
      10
    )
    const customNote = formValues?.customNote || ''

    if (!validate(amlValuePredefinedReasonId, customNote, newScore)) {
      return
    }
    if (!formValues) {
      return
    }
    try {
      const newFormValues = {
        newScore,
        amlValuePredefinedReasonId,
        customNote,
      }
      await clientsApiClient.addAMLAdjustmentRecord(newFormValues, data.id)
      setToast(successToast('Adjustment added successfully'))
      callback()
      setFormValues({ amlValuePredefinedReasonId: 0, newScore: undefined, customNote: '' })
    } catch (err) {
      console.error(err)
      setToast(errorToast('Error adding adjustment'))
    }
  }

  const validate = useCallback((reasonId: number, customNote: string, newScore: number) => {
    return !(!reasonId || !customNote || !newScore)
  }, [])

  return { handleSubmit, formValues, setFormValues }
}

const useMapStateToProps = () => ({
  amlCategories: useAppSelector((state) => state.clients.amlCategories),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const clientsApiClient = useApiClient(ClientsApiClient)

  return {
    fetchAMLCategories: async () => dispatch(fetchAMLCategoriesTypes(clientsApiClient)),
  }
}

export const ClientDetailsAMLScore: React.FC<ClientDetailsAMLScoreProps> = (props) => {
  const { data, onHistoryOpen } = props

  const clientsApiClient = useApiClient(ClientsApiClient)
  const setToast = useContext(ToastContext)

  const [isLockedScore, setIsLockedScore] = useState(false)

  const { amlData, adjustmentReasons, calculateScore, forceRefresh } =
    useClientDetailsAMLScoreFetch(props)
  const form = useClientDetailsAMLScoreMutate(props)

  const { amlCategories } = useMapStateToProps()
  const { fetchAMLCategories } = useMapDispatchToProps()

  const { formValues, setFormValues } = form

  useEffect(() => {
    ;(async () => {
      if (isEmpty(amlCategories)) await fetchAMLCategories()
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = (event: FormEvent) => {
    form.handleSubmit(event, () => {
      forceRefresh()
    })
  }

  const handleDismiss = async (adjustmentId: string) => {
    await clientsApiClient.dismissAMLAdjustmentRecord(adjustmentId, data.id)
    setToast(successToast('Adjustment dismissed successfully'))
    forceRefresh()
  }

  const handleDetailedHistory = () => {
    onHistoryOpen()
  }

  return (
    <Container>
      <FormBaseColumn style={{ width: '100%' }}>
        <FormBaseSectionBlock>
          <FormBaseFieldLabel>Scores</FormBaseFieldLabel>
          <FormFieldBlock>
            <FormBaseRow>
              <FormFieldLabel>Name</FormFieldLabel>
              <FormFieldGroup>
                <b>Score</b>
              </FormFieldGroup>
            </FormBaseRow>
          </FormFieldBlock>
          {amlData?.amlFields.map((f) => (
            <FormFieldBlock key={f.id}>
              <FormBaseRow>
                <FormFieldLabel>{f.displayName}</FormFieldLabel>
                <FormFieldGroup>
                  <input type='number' data-test={f.displayName} value={f.currentValue.score} />
                </FormFieldGroup>
              </FormBaseRow>
            </FormFieldBlock>
          ))}
          <FormFieldBlock>
            <FormBaseRow>
              <FormFieldLabel>Total</FormFieldLabel>
              <FormFieldGroup>
                <b>{calculateScore.score}</b>
              </FormFieldGroup>
            </FormBaseRow>
          </FormFieldBlock>
        </FormBaseSectionBlock>
        <FormBaseSectionBlock>
          <FormBaseFieldLabel>Manual Adjustments</FormBaseFieldLabel>
          <Grid data={amlData?.manualAdjustments.values || []}>
            <Column field='reason.name' title='Reason' className='table-columns__content' />
            <Column field='score' title='Score' className='table-columns__content' />
            <Column
              width={50}
              title=''
              cell={(gridCellProps: GridCellProps) => (
                <td>
                  <MoreButton
                    items={[
                      {
                        name: 'Dismiss',
                        'data-test': 'dismiss',
                        onClick: () => handleDismiss(gridCellProps.dataItem.id),
                      },
                    ]}
                    data-test='more-button'
                  />
                </td>
              )}
            />
          </Grid>
          <FormFieldBlock>
            <FormBaseRow>
              <FormFieldLabel>Adjusted Score</FormFieldLabel>
              <FormFieldGroup>
                <b>{calculateScore?.adjustedScore}</b>
              </FormFieldGroup>
            </FormBaseRow>
          </FormFieldBlock>
        </FormBaseSectionBlock>
        <FormBaseSectionBlock>
          <form onSubmit={handleSubmit}>
            <FormBaseFieldLabel>Add New</FormBaseFieldLabel>
            <FormFieldBlock>
              <FormBaseRow>
                <FormFieldLabel>Custom Notes</FormFieldLabel>
                <FormFieldGroup>
                  <input
                    type='text'
                    data-test='add-new-adjustment-type'
                    value={formValues?.customNote}
                    onChange={(event) => {
                      setFormValues({ ...formValues, customNote: event.target.value })
                    }}
                  />
                </FormFieldGroup>
              </FormBaseRow>
            </FormFieldBlock>
            <FormFieldBlock>
              <FormBaseRow>
                <FormFieldLabel>Reason</FormFieldLabel>
                <FormFieldGroup>
                  <select
                    value={formValues?.amlValuePredefinedReasonId}
                    onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                      const adjustmentReason = adjustmentReasons.find(
                        (r) => r.id === Number(event.target.value)
                      )
                      if (!adjustmentReason) {
                        return
                      }
                      if (adjustmentReason.score) {
                        setIsLockedScore(true)
                        setFormValues({
                          ...formValues,
                          amlValuePredefinedReasonId: adjustmentReason.id,
                          newScore: (adjustmentReason.score || '').toString(),
                        })
                      } else {
                        setIsLockedScore(false)
                        setFormValues({
                          ...formValues,
                          amlValuePredefinedReasonId: adjustmentReason.id,
                        })
                      }
                    }}
                  >
                    <option value={''}>Not set</option>
                    {(adjustmentReasons || []).map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </FormFieldGroup>
              </FormBaseRow>
            </FormFieldBlock>
            <FormFieldBlock>
              <FormBaseRow>
                <FormFieldLabel>Adjustment</FormFieldLabel>
                <FormFieldGroup>
                  <input
                    type='number'
                    value={formValues?.newScore || ''}
                    readOnly={isLockedScore}
                    disabled={isLockedScore}
                    onKeyDown={(event) => {
                      return ['e', 'E', '+'].includes(event.key) && event.preventDefault()
                    }}
                    onChange={(event) => {
                      setFormValues({ ...formValues, newScore: event.target.value })
                    }}
                    data-test='add-new-adjustment'
                  />
                </FormFieldGroup>
              </FormBaseRow>
            </FormFieldBlock>
            <FormBaseButtonContainer>
              <Button
                key='button'
                type='button'
                onClick={() =>
                  setFormValues({
                    amlValuePredefinedReasonId: 0,
                    newScore: undefined,
                    customNote: '',
                  })
                }
              >
                Clear
              </Button>
              <Button key='submit' type='submit'>
                Save
              </Button>
              <Button key='history' type='button' onClick={handleDetailedHistory}>
                History
              </Button>
            </FormBaseButtonContainer>
          </form>
        </FormBaseSectionBlock>
      </FormBaseColumn>
    </Container>
  )
}
